import { useState } from "react";
import { Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useFetch } from "../lib/api";
import { money, fmtDate, STATUS_LABELS, PROVINCES, imgUrl } from "../lib/shop";
import { AdminTitle, Toggle } from "./AdminLayout";
import { PayBadge } from "./Overview";
import { UploadButton } from "./Images";

export function Customers() {
  const { data } = useFetch("/admin/customers");
  const [open, setOpen] = useState(null);
  return (
    <div>
      <AdminTitle title="Customers" />
      <p className="text-xs text-zinc-500 mb-4">Private customer information — visible to admins only. Handle in line with POPIA.</p>
      <div className="space-y-2" data-testid="admin-customers-list">
        {(data || []).map((c) => (
          <div key={c.email} className="card-dark p-4">
            <button className="w-full flex flex-wrap justify-between gap-2 text-left" onClick={() => setOpen(open === c.email ? null : c.email)} data-testid={`admin-customer-${c.email}`}>
              <span><b>{c.name}</b> {c.registered && <span className="text-[10px] bg-[#d4af37]/20 text-gold px-1.5 py-0.5 rounded">ACCOUNT</span>}<span className="block text-xs text-zinc-400">{c.email} · {c.phone}</span></span>
              <span className="text-right text-sm"><b>{money(c.total_spent)}</b><span className="block text-xs text-zinc-400">{c.order_count} order(s)</span></span>
            </button>
            {open === c.email && (
              <div className="mt-3 border-t border-white/10 pt-3 space-y-1">
                {c.orders.map((o) => <p key={o.order_number} className="text-sm flex flex-wrap gap-3 items-center"><a href={`/admin/orders?open=${o.order_number}`} className="text-gold font-bold">{o.order_number}</a><span className="text-zinc-400">{fmtDate(o.created_at)}</span><span>{money(o.total)}</span><PayBadge s={o.payment_status} /><span className="text-zinc-400">{STATUS_LABELS[o.status]}</span></p>)}
                {!c.orders.length && <p className="text-xs text-zinc-500">No orders yet.</p>}
              </div>
            )}
          </div>
        ))}
        {data?.length === 0 && <p className="text-zinc-500">No customers yet.</p>}
      </div>
    </div>
  );
}

function CrudSection({ title, path, empty, fields, render, testid }) {
  const { data, reload } = useFetch(path);
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : v]));
    try {
      if (editing) await api.put(`${path}/${editing}`, body); else await api.post(path, body);
      toast.success("Saved"); setF(empty); setEditing(null); reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this item?")) return; await api.delete(`${path}/${id}`); reload(); };
  return (
    <section className="grid xl:grid-cols-[1fr_400px] gap-6 mb-12" data-testid={testid}>
      <div className="space-y-2">
        <h2 className="font-display text-3xl mb-2">{title}</h2>
        {(data || []).map((d) => (
          <div key={d.id} className="card-dark p-4 flex items-center gap-3">
            <div className="flex-1 min-w-0 text-sm">{render(d)}</div>
            <button onClick={() => { setEditing(d.id); setF({ ...empty, ...Object.fromEntries(Object.keys(empty).map((k) => [k, d[k] ?? ""])) }); }} className="p-2" aria-label="Edit" data-testid={`${testid}-edit-${d.id}`}><Pencil size={16} /></button>
            <button onClick={() => remove(d.id)} className="p-2 text-red-400" aria-label="Delete" data-testid={`${testid}-delete-${d.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <form onSubmit={save} className="card-dark p-5 space-y-3 h-fit">
        <p className="font-display text-2xl">{editing ? "Edit" : "Add new"}</p>
        {fields(f, setF)}
        <div className="flex gap-2">
          <button className="btn-gold flex-1 !min-h-[44px]" data-testid={`${testid}-save`}>Save</button>
          {editing && <button type="button" className="btn-outline !min-h-[44px]" onClick={() => { setEditing(null); setF(empty); }}>Cancel</button>}
        </div>
      </form>
    </section>
  );
}

const In = ({ f, setF, k, l, type = "text", ...rest }) => (
  <div><label className="label">{l}</label><input type={type} className="field" value={f[k] ?? ""} data-testid={`field-${k.replace(/_/g, "-")}`}
    onChange={(e) => setF({ ...f, [k]: type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })} {...rest} /></div>
);

export function Promotions() {
  return (
    <div>
      <AdminTitle title="Promotions" />
      <CrudSection title="Discount codes" path="/admin/discounts" testid="discounts"
        empty={{ code: "", type: "percent", value: "", min_order: "", usage_limit: "", start_date: "", end_date: "", active: true }}
        render={(d) => <><b className="text-gold">{d.code}</b> — {d.type === "percent" ? `${d.value}% off` : `${money(d.value)} off`}
          <span className="block text-xs text-zinc-400">{d.min_order ? `Min ${money(d.min_order)} · ` : ""}Used {d.used_count || 0}{d.usage_limit ? `/${d.usage_limit}` : ""} · {d.start_date || "now"} → {d.end_date || "no end"} · {d.active ? "Active" : "Inactive"}</span></>}
        fields={(f, setF) => (<>
          <In f={f} setF={setF} k="code" l="Code" required />
          <div><label className="label">Type</label><select className="field" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="field-type"><option value="percent">Percentage (%)</option><option value="fixed">Fixed amount (R)</option></select></div>
          <In f={f} setF={setF} k="value" l="Value" type="number" required min="0.01" step="0.01" />
          <In f={f} setF={setF} k="min_order" l="Minimum order (R)" type="number" min="0" />
          <In f={f} setF={setF} k="usage_limit" l="Usage limit" type="number" min="1" />
          <div className="grid grid-cols-2 gap-2"><In f={f} setF={setF} k="start_date" l="Start date" type="date" /><In f={f} setF={setF} k="end_date" l="End date" type="date" /></div>
          <Toggle label="Active" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />
        </>)} />
      <CrudSection title="Homepage banners & sale campaigns" path="/admin/promotions" testid="banners"
        empty={{ title: "", subtitle: "", cta_text: "SHOP NOW", cta_link: "/sale", image: "", start_date: "", end_date: "", active: true }}
        render={(d) => <div className="flex gap-3 items-center">{d.image && <img src={imgUrl(d.image)} alt="" className="w-16 h-12 object-cover rounded" />}<span><b>{d.title}</b> · {d.subtitle}<span className="block text-xs text-zinc-400">{d.cta_text} → {d.cta_link} · {d.start_date || "now"} → {d.end_date || "no end"} · {d.active ? "Active" : "Inactive"}</span></span></div>}
        fields={(f, setF) => (<>
          <In f={f} setF={setF} k="title" l="Headline" required />
          <In f={f} setF={setF} k="subtitle" l="Sub-headline" />
          <div className="grid grid-cols-2 gap-2"><In f={f} setF={setF} k="cta_text" l="Button text" /><In f={f} setF={setF} k="cta_link" l="Button link" placeholder="/sale" /></div>
          <div className="flex items-center gap-3">{f.image && <img src={imgUrl(f.image)} alt="" className="w-20 h-14 object-cover rounded" />}<UploadButton multiple={false} label="Banner image" testid="banner-image-upload" onUploaded={(a) => a[0] && setF({ ...f, image: a[0].url })} /></div>
          <div className="grid grid-cols-2 gap-2"><In f={f} setF={setF} k="start_date" l="Start date" type="date" /><In f={f} setF={setF} k="end_date" l="End date" type="date" /></div>
          <Toggle label="Active" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />
          <p className="text-xs text-zinc-500">The newest active banner within its dates shows on the homepage. For sale campaigns, mark products "On sale" with a sale price and link the banner to /sale.</p>
        </>)} />
    </div>
  );
}

export function Delivery() {
  return (
    <div>
      <AdminTitle title="Delivery Zones" />
      <p className="text-sm text-zinc-400 mb-6 max-w-2xl">Manage your delivery options and fees (set the fee to 0 for FREE). Uber options ask the customer for a delivery address in Johannesburg; PAXI options ask for the PEP store and mall where they'll collect.</p>
      <CrudSection title="Zones" path="/admin/delivery-zones" testid="zones"
        empty={{ name: "", method: "courier", description: "", provinces: [], fee: "", free_over: "", eta: "", active: true, sort: 0 }}
        render={(z) => <><b>{z.name}</b> <span className="text-[10px] uppercase bg-white/10 px-1.5 py-0.5 rounded">{z.method || "courier"}</span> — {z.fee ? money(z.fee) : "FREE"}{z.free_over ? ` · free over ${money(z.free_over)}` : ""}<span className="block text-xs text-zinc-400">{z.provinces?.length ? z.provinces.join(", ") : "All provinces"} · {z.eta} · {z.active ? "Active" : "Inactive"}</span></>}
        fields={(f, setF) => (<>
          <In f={f} setF={setF} k="name" l="Option name" required />
          <div><label className="label">Delivery type</label><select className="field" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="field-method">
            <option value="uber">Uber (asks for delivery address, Johannesburg 20km)</option><option value="paxi">PAXI (asks for PEP store &amp; mall)</option><option value="courier">Courier (asks for delivery address)</option></select></div>
          <In f={f} setF={setF} k="description" l="Description" />
          <In f={f} setF={setF} k="fee" l="Delivery fee (R)" type="number" required min="0" step="0.01" />
          <In f={f} setF={setF} k="free_over" l="Free delivery over (R, optional)" type="number" min="0" />
          <In f={f} setF={setF} k="eta" l="Estimated delivery time" placeholder="e.g. 2–4 working days" />
          <In f={f} setF={setF} k="sort" l="Display order" type="number" />
          <div><p className="label">Limit to provinces (optional)</p><div className="flex flex-wrap gap-1.5">
            {PROVINCES.map((p) => { const on = (f.provinces || []).includes(p); return <button type="button" key={p} className={`chip !min-h-[36px] !text-xs ${on ? "chip-active" : ""}`} onClick={() => setF({ ...f, provinces: on ? f.provinces.filter((x) => x !== p) : [...(f.provinces || []), p] })}>{p}</button>; })}
          </div></div>
          <Toggle label="Active" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />
        </>)} />
    </div>
  );
}

export function PepStores() {
  const [rows, setRows] = useState("");
  const [key, setKey] = useState(0);
  const importRows = async () => {
    try { const r = await api.post("/admin/pep-stores/import", { rows }); toast.success(`Added ${r.data.added}, skipped ${r.data.skipped}`); setRows(""); setKey((k) => k + 1); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4">
      <h2 className="font-display text-4xl mb-2">PEP stores for PAXI</h2>
      <p className="text-sm text-zinc-400 mb-6 max-w-2xl">Customers choosing PAXI search this list at checkout. They can still type a store manually if it's missing.</p>
      <div className="card-dark p-5 mb-8 max-w-3xl space-y-3" data-testid="pep-import">
        <p className="font-display text-2xl">Bulk add (one store per line)</p>
        <p className="text-xs text-zinc-500">Format: Store name, Mall name, City, Province, PAXI code (optional)</p>
        <textarea rows={5} className="field py-3 font-mono text-xs" value={rows} onChange={(e) => setRows(e.target.value)} placeholder="PEP Example Store, Example Mall, Johannesburg, Gauteng, P123" data-testid="pep-import-textarea" />
        <button type="button" onClick={importRows} disabled={rows.trim().length < 3} className="btn-outline !min-h-[44px]" data-testid="pep-import-button">Import stores</button>
      </div>
      <CrudSection key={key} title="Stores" path="/admin/pep-stores" testid="pep-stores"
        empty={{ name: "", mall_name: "", city: "", province: "Gauteng", paxi_code: "", active: true }}
        render={(s) => <><b>{s.name}</b><span className="block text-xs text-zinc-400">{[s.mall_name, s.city, s.province].filter(Boolean).join(" · ")}{s.paxi_code ? ` · PAXI ${s.paxi_code}` : ""} · {s.active ? "Active" : "Hidden"}</span></>}
        fields={(f, setF) => (<>
          <In f={f} setF={setF} k="name" l="PEP store name" required />
          <In f={f} setF={setF} k="mall_name" l="Mall / centre" />
          <In f={f} setF={setF} k="city" l="City / Town" required />
          <div><label className="label">Province</label><select className="field" value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} data-testid="field-province">{PROVINCES.map((p) => <option key={p}>{p}</option>)}</select></div>
          <In f={f} setF={setF} k="paxi_code" l="PAXI point code (optional)" />
          <Toggle label="Active" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />
        </>)} />
    </div>
  );
}

export function Categories() {
  return (
    <CrudSection title="Product categories" path="/admin/categories" testid="categories" empty={{ name: "", sort: 0 }}
      render={(c) => <b>{c.name}</b>}
      fields={(f, setF) => (<><In f={f} setF={setF} k="name" l="Category name" required /><In f={f} setF={setF} k="sort" l="Display order" type="number" /></>)} />
  );
}
